import { useState } from 'react'
import { X } from 'lucide-react'

import { useAppStore } from '@core/store'
import SeparadorInventario from './SeparadorInventario'
import SeparadorImportar from './SeparadorImportar'
import SeparadorPsets from './SeparadorPsets'
import SeparadorAlteracoes from './SeparadorAlteracoes'

type Separador = 'inventario' | 'importar' | 'psets' | 'alteracoes'

const NOMES: Record<Separador, string> = {
  inventario: 'Inventário',
  importar: 'Importar de Excel',
  psets: 'Psets',
  alteracoes: 'Alterações',
}

/** Dados do IFC: ver o que o ficheiro tem, acrescentar a partir de
 *  Excel, mudar nomes e apagar Psets, e gravar um IFC novo.
 *
 *  Vive dentro do hub e não à parte porque trabalha sobre o modelo
 *  aberto: o inventário é do modelo que se está a ver, e o resultado
 *  volta a abrir-se no mesmo sítio para se conferir. */
export default function PainelDados({ aoFechar }: { aoFechar: () => void }) {
  const [sep, setSep] = useState<Separador>('inventario')
  const modelo = useAppStore((s) => (s.activeModelId ? s.models.find((m) => m.id === s.activeModelId) ?? null : s.models[0] ?? null))
  const pendentes = useAppStore(
    (s) =>
      s.ifcEditLotes.length +
      Object.keys(s.ifcEditAttributeEdits).length +
      Object.keys(s.ifcEditClassChanges).length +
      Object.keys(s.ifcEditPsetEdits).length,
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1">
        <span className="text-xs font-medium text-[var(--color-text)]">Dados do IFC</span>
        {modelo && <span className="ml-1 truncate text-[10px] text-[var(--color-text-muted)]">{modelo.fileName}</span>}
        <button type="button" onClick={aoFechar} className="ml-auto rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" title="Fechar">
          <X size={14} />
        </button>
      </div>
      <div className="flex gap-0.5 border-b border-[var(--color-border)] px-2 pt-1">
        {(Object.keys(NOMES) as Separador[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSep(s)}
            className={`rounded-t px-2 py-1 text-[11px] ${
              sep === s
                ? 'border border-b-0 border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {NOMES[s]}
            {s === 'alteracoes' && pendentes > 0 && (
              <span className="ml-1 rounded bg-[var(--color-accent-bg)] px-1 text-[9px] text-[var(--color-accent)]">{pendentes}</span>
            )}
          </button>
        ))}
      </div>
      {!modelo ? (
        <p className="p-3 text-[11px] text-[var(--color-text-muted)]">Abre um IFC para ver e editar os dados.</p>
      ) : sep === 'inventario' ? (
        <SeparadorInventario modelId={modelo.id} ficheiro={modelo.fileName} />
      ) : sep === 'importar' ? (
        <SeparadorImportar modelId={modelo.id} aoJuntar={() => setSep('alteracoes')} />
      ) : sep === 'psets' ? (
        <SeparadorPsets modelId={modelo.id} aoJuntar={() => setSep('alteracoes')} />
      ) : (
        <SeparadorAlteracoes modelId={modelo.id} ficheiro={modelo.fileName} />
      )}
    </div>
  )
}
